'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSession } from 'next-auth/react';
import { LogoutModal } from '../LogoutModal';
import { logout } from '@/app/actions/auth/logout';

const INACTIVITY_TIME_USERS = 30 * 60 * 1000;
const DEFAULT_EVENTS = [
  'mousemove',
  'mousedown',
  'keydown',
  'scroll',
  'touchstart',
  'touchmove',
  'touchend',
  'visibilitychange',
  'focus',
] as const;

type EventType = (typeof DEFAULT_EVENTS)[number];

const Inactivity = () => {
  const { data: session } = useSession();
  const userEmailRef = useRef<string | undefined | null>();
  const inactivityTimerRef = useRef<number | null>(null);
  const [openLogoutModal, setOpenLogoutModal] = useState<boolean>(false);

  const isDevMode = process.env.NODE_ENV === 'development';

  const getLastClosedTimeKey = useCallback(() => `last-closed-time-${userEmailRef.current}`, []);
  const clearTimer = () => inactivityTimerRef.current && clearTimeout(inactivityTimerRef.current);

  const handleInactivity = useCallback(async () => {
    if (userEmailRef.current) {
      localStorage.removeItem(getLastClosedTimeKey());
      await logout();
      userEmailRef.current = null;
      setOpenLogoutModal(true);
    }
  }, [getLastClosedTimeKey]);

  const resetInactivityTimer = useCallback(() => {
    localStorage.removeItem(getLastClosedTimeKey());
    clearTimer();
    inactivityTimerRef.current = window.setTimeout(() => {
      handleInactivity().catch((error) => console.error('Error handling inactivity:', error));
    }, INACTIVITY_TIME_USERS);
  }, [getLastClosedTimeKey, handleInactivity]);

  const validateInactivity = useCallback(async () => {
    const lastClosedTime = localStorage.getItem(getLastClosedTimeKey());
    if (lastClosedTime && Date.now() - Number(lastClosedTime) > INACTIVITY_TIME_USERS) {
      handleInactivity();
    } else {
      resetInactivityTimer();
    }
  }, [getLastClosedTimeKey, handleInactivity, resetInactivityTimer]);

  const handleBeforeUnload = useCallback(() => {
    if (userEmailRef.current) {
      localStorage.setItem(getLastClosedTimeKey(), Date.now().toString());
    }
  }, [getLastClosedTimeKey]);

  const handleVisibilityChange = useCallback(() => {
    document.hidden ? handleBeforeUnload() : validateInactivity();
  }, [handleBeforeUnload, validateInactivity]);

  useEffect(() => {
    const setCurrentUserEmail = () => {
      userEmailRef.current = session?.user?.email;
      validateInactivity();
      DEFAULT_EVENTS.forEach((event: EventType) => {
        const handler = event === 'visibilitychange' ? handleVisibilityChange : resetInactivityTimer;
        const target = event === 'visibilitychange' ? document : window;
        target.addEventListener(event, handler);
      });
      window.addEventListener('beforeunload', handleBeforeUnload);
    };

    if (session && !isDevMode) setCurrentUserEmail();

    return () => {
      DEFAULT_EVENTS.forEach((event) => {
        const handler = event === 'visibilitychange' ? handleVisibilityChange : resetInactivityTimer;
        const target = event === 'visibilitychange' ? document : window;
        target.removeEventListener(event, handler);
      });
      clearTimer();
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [session, validateInactivity, handleVisibilityChange, handleBeforeUnload, isDevMode, resetInactivityTimer]);

  return <LogoutModal isOpen={openLogoutModal} onClose={() => setOpenLogoutModal(false)} />;
};

export default Inactivity;
